import React, { ReactNode } from "react";
import { Tooltip, MenuItem, ListItemIcon } from "@mui/material";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import styles from "@/components/tables/genericCrudTable.module.css";

export type RowActionsLayout = "menu" | "buttons";

export interface RowActionDescriptor {
  key: string | number;
  icon: ReactNode;
  menuIcon?: ReactNode;
  label: ReactNode;
  tooltip?: ReactNode;
  onClick: (event: React.MouseEvent) => void;
  disabled?: boolean;
  color?: "iconBase" | "error";
  wrapButtonInSpan?: boolean;
  showNotAllowedCursorWhenDisabled?: boolean;
  suppressTooltipWhenDisabled?: boolean;
  testId?: string;
}

interface RowActionItemProps {
  action: RowActionDescriptor;
  layout: RowActionsLayout;
  [menuItemProp: string]: unknown;
}

const RowActionItem = ({
  action,
  layout,
  ...menuItemProps
}: RowActionItemProps) => {
  const disabled = Boolean(action.disabled);

  if (layout === "menu") {
    return (
      <MenuItem
        {...menuItemProps}
        data-testid={action.testId ?? menuItemProps["data-testid"]}
        onClick={action.onClick}
        disabled={disabled}
      >
        <ListItemIcon>{action.menuIcon ?? action.icon}</ListItemIcon>
        {action.label}
      </MenuItem>
    );
  }

  const suppressTooltip =
    disabled && Boolean(action.suppressTooltipWhenDisabled);
  const button = (
    <GenericButton
      data-testid={action.testId}
      variant="icon"
      color={action.color ?? "iconBase"}
      onClick={action.onClick}
      disabled={disabled}
    >
      {action.icon}
    </GenericButton>
  );

  return (
    <Tooltip
      arrow
      placement="top"
      enterDelay={1000}
      enterNextDelay={250}
      title={action.tooltip ?? action.label}
      disableHoverListener={suppressTooltip}
      disableFocusListener={suppressTooltip}
      disableTouchListener={suppressTooltip}
    >
      {action.wrapButtonInSpan ? (
        <span
          className={
            disabled && action.showNotAllowedCursorWhenDisabled
              ? styles.cursorNotAllowed
              : undefined
          }
        >
          {button}
        </span>
      ) : (
        button
      )}
    </Tooltip>
  );
};

export default RowActionItem;
